import OlModify, { ModifyEvent } from 'ol/interaction/Modify';
import {
  closestOnSegment,
  distance as coordinateDistance,
  equals as coordinatesEqual,
  squaredDistance as squaredCoordinateDistance,
  squaredDistanceToSegment
} from 'ol/coordinate';
import { altKeyOnly, shiftKeyOnly, singleClick } from 'ol/events/condition';
import {
  boundingExtent,
  buffer as bufferExtent,
  createOrUpdateFromCoordinate as createExtent
} from 'ol/extent';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import Style from 'ol/style/Style';
import { getUid } from 'ol/util';
import { unByKey } from 'ol/Observable';
import { fromUserCoordinate, fromUserExtent, toUserExtent } from 'ol/proj';
import Event from 'ol/events/Event';

import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { SketchTextInput } from 'cadenza/sketch-text-input/sketch-text-input';
import { animateDuringMapMove } from 'cadenza/map/map-utils';
import { SketchFeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import { FeatureProperty } from 'cadenza/map/feature-property';
import { Rectangle } from 'cadenza/map/geom/rectangle';
import { Oval, ovalGeometryFunction } from 'cadenza/map/geom/oval';
import { Line } from 'cadenza/map/geom/line';
import {
  isPolygonAssignableOrMultiPolygon
} from 'cadenza/map/geometry-edit-strategy/union-without-intersection-strategy';
import { getWebWorkerInstance } from 'cadenza/map/geometry-edit-strategy/union-without-intersection-worker-api';
import { logger } from 'cadenza/utils/logging';
import { getSketchItemDto, setSketchItemDto, SketchItemType } from 'cadenza/workbook/sketch/sketch-layer/sketch-types';

const CIRCLE_CIRCUMFERENCE_INDEX = 1;

export const deleteCondition = event => {
  return singleClick(event) && (altKeyOnly(event) || shiftKeyOnly(event));
};

// Same as the ModifyEventType from ol/interaction/Modify.js
export const ModifyEventType = {
  MODIFYSTART: 'modifystart',
  MODIFYEND: 'modifyend'
};

export const CadenzaModifyEventType = {
  MODIFYEND: `cadenza-${ModifyEventType.MODIFYEND}`
};

export class ModifyInteraction extends OlModify {

  _mapTargetElement;
  _type;
  _source;
  _eventKeys;
  _keepRectangleAspectRatioCondition;

  constructor (options) {
    const insertVertexCondition = options.insertVertexCondition || (() => true);
    options.deleteCondition = deleteCondition; // default delete condition (Alt+Click) does not work on some Linuxes
    options.insertVertexCondition = e => this._insertVertexCondition() && insertVertexCondition(e);
    super(options);
    this._mapTargetElement = options.map?.getTargetElement();
    this._type = options.type;
    this._source = options.source;
    this._keepRectangleAspectRatioCondition = options.keepRectangleAspectRatioCondition || (() => false);
    this.SEGMENT_WRITERS_.Rectangle = this._writeRectangleGeometry.bind(this);
    this.setActive(false);
    this._sketchLayer = options.sketchLayer;

    this.on('change:active', (event) => {
      if (!event.oldValue) {
        this._onActivation(options.withGeometryValidation);
      } else {
        this._onDeactivation();
      }
    });
  }

  _onActivation (withGeometryValidation) {
    this._eventKeys = this._setInteractionListeners(withGeometryValidation);
  }

  _onDeactivation () {
    unByKey(this._eventKeys);
  }

  _setInteractionListeners (withGeometryValidation) {
    const eventKeys = [
      this.on(ModifyEventType.MODIFYSTART, ({ features }) => onFeaturesModifyStart(features))
    ];

    // If we don't care about geometry validation, don't add this listener, since it could interact with the sketch 'modifyend' listener
    if (withGeometryValidation && isPolygonAssignableOrMultiPolygon(this._type)) {
      eventKeys.push(this.on(ModifyEventType.MODIFYEND, ({ features }) => {
        onFeaturesModifyEnd(features.getArray(), this._source, this._mapTargetElement);
      }));
    }
    return eventKeys;
  }

  // @Overrides ol.interaction.Modify.handlePointerAtPixel_
  // It is almost an identical copy of the function handlePointerAtPixel_ with the only exception of
  // custom pointDistanceToSegmentDataSquared and closestOnSegmentData function implementations.
  handlePointerAtPixel_ (pixel, map, optCoordinate) {
    const pixelCoordinate = optCoordinate || map.getCoordinateFromPixel(pixel);
    const projection = map.getView().getProjection();
    const sortByDistance = function (a, b) {
      return projectedDistanceToSegmentDataSquared(pixelCoordinate, a, projection)
        - projectedDistanceToSegmentDataSquared(pixelCoordinate, b, projection);
    };
    const viewExtent = fromUserExtent(createExtent(pixelCoordinate), projection);
    const buffer = map.getView().getResolution() * this.pixelTolerance_;
    const box = toUserExtent(bufferExtent(viewExtent, buffer), projection);
    const rBush = this.rBush_;
    const nodes = rBush.getInExtent(box);
    if (nodes.length > 0) {
      nodes.sort(sortByDistance);
      const node = nodes[0];
      const closestSegment = node.segment;
      let vertex = closestOnSegmentData(pixelCoordinate, node, projection);
      const vertexPixel = map.getPixelFromCoordinate(vertex);
      let dist = coordinateDistance(pixel, vertexPixel);
      if (dist <= this.pixelTolerance_) {
        /** @type {Object<string, boolean>} */
        const vertexSegments = {};
        if (node.geometry.getType() === GeometryType.CIRCLE && node.index === CIRCLE_CIRCUMFERENCE_INDEX) {
          this.snappedToVertex_ = true;
          this.createOrUpdateVertexFeature_(vertex);
        } else {
          const pixel1 = map.getPixelFromCoordinate(closestSegment[0]);
          const pixel2 = map.getPixelFromCoordinate(closestSegment[1]);
          const squaredDist1 = squaredCoordinateDistance(vertexPixel, pixel1);
          const squaredDist2 = squaredCoordinateDistance(vertexPixel, pixel2);
          dist = Math.sqrt(Math.min(squaredDist1, squaredDist2));
          this.snappedToVertex_ = dist <= this.pixelTolerance_;
          if (this.snappedToVertex_) {
            vertex = squaredDist1 > squaredDist2 ? closestSegment[1] : closestSegment[0];
          }
          this.createOrUpdateVertexFeature_(vertex);
          for (let i = 1, ii = nodes.length; i < ii; ++i) {
            const segment = nodes[i].segment;
            if (((coordinatesEqual(closestSegment[0], segment[0]) && coordinatesEqual(closestSegment[1], segment[1]))
              || (coordinatesEqual(closestSegment[0], segment[1]) && coordinatesEqual(closestSegment[1], segment[0])))) {
              vertexSegments[getUid(segment)] = true;
            } else {
              break;
            }
          }
        }
        vertexSegments[getUid(closestSegment)] = true;
        this.vertexSegments_ = vertexSegments;
        return;
      }
    }
    if (this.vertexFeature_) {
      this.overlay_.getSource().removeFeature(this.vertexFeature_);
      this.vertexFeature_ = null;
    }
  }

  // @Overrides ol.interaction.Modify.createOrUpdateVertexFeature_
  createOrUpdateVertexFeature_ (coordinates) {
    // OpenLayers doesn't do a check for insertVertexCondition on mouse hover and creates a draggable vertex anyways. insertVertexCondition check is done only on point dragging, which is too late.
    // In order to not add a new handle vertex, when the insert vertex operation is not allowed, we override the function createOrUpdateVertexFeature_ and do an additional check here.
    if (!this.snappedToVertex_ && !this.insertVertexCondition_(this.lastPointerEvent_)) {
      if (this.vertexFeature_) {
        this.overlay_.getSource().removeFeature(this.vertexFeature_);
        this.vertexFeature_ = null;
      }
      return null;
    }
    return super.createOrUpdateVertexFeature_(coordinates);
  }

  // @Overrides ol.interaction.Modify.handleDragEvent
  handleDragEvent (evt) {
    let vertex = evt.coordinate;

    const ovalDragSegments = this.dragSegments_.filter(dragSegment => dragSegment[0].geometry instanceof Oval);
    if (ovalDragSegments.length > 0) {
      // if there are ovals between geometries being modified, modify only the ovals
      this.ignoreNextSingleClick_ = false;
      this.willModifyFeatures_(evt, ovalDragSegments);
      for (let i = 0, ii = ovalDragSegments.length; i < ii; ++i) {
        this._handleOvalVertexDrag(ovalDragSegments[i], vertex);
      }
    }

    const rectangleDragSegments = this.dragSegments_.filter(dragSegment => dragSegment[0].geometry instanceof Rectangle);
    if (rectangleDragSegments.length > 0) {
      this.willModifyFeatures_(evt, rectangleDragSegments);

      rectangleDragSegments.forEach(rectangleDragSegment => {
        vertex = this._handleRectangleVertexDrag(rectangleDragSegment, vertex);
      });
    }

    const originalDragSegments = this.dragSegments_;
    this.dragSegments_ = this.dragSegments_.filter(dragSegment => !(dragSegment[0].geometry instanceof Oval) && !(dragSegment[0].geometry instanceof Rectangle));
    super.handleDragEvent(...arguments);
    this.dragSegments_ = originalDragSegments;

    this.createOrUpdateVertexFeature_(vertex);
  }

  _handleOvalVertexDrag (dragSegment, vertex) {
    const segmentData = dragSegment[0];
    const geometry = segmentData.geometry;
    const segment = segmentData.segment;

    segment[0] = vertex;
    segment[1] = vertex;
    if (segmentData.index === 0) { // We're dragging the ovals's center
      this.changingFeature_ = true;
      geometry.setCenter(vertex);
      this.changingFeature_ = false;
    } else { // We're dragging the ovals's circumference:
      this.changingFeature_ = true;
      const center = geometry.getCenter();
      ovalGeometryFunction([ center, vertex ], geometry, this._shiftPressed());
      this.changingFeature_ = false;
    }
  }

  _handleRectangleVertexDrag ([ segmentData, segmentPointIndex ], vertex) {
    const geometry = segmentData.geometry;
    const segment = segmentData.segment;

    this.changingFeature_ = true;
    const keepAspectRatio = this._keepRectangleAspectRatioCondition(segmentData.feature);
    vertex = geometry.updateVertex(segmentData.index, vertex, this._shiftPressed(), keepAspectRatio);
    this.changingFeature_ = false;

    segment[segmentPointIndex] = vertex;
    return vertex;
  }

  // @Overrides ol.interaction.Modify.handleUpEvent
  handleUpEvent (evt) {
    const rectangleFeatures = new Set(this.dragSegments_
      .filter(dragSegment => dragSegment[0].geometry instanceof Rectangle)
      .map(dragSegment => dragSegment[0].feature));
    rectangleFeatures.forEach(feature => feature.getGeometry().normalize());

    const originalDragSegments = this.dragSegments_;
    this.dragSegments_ = this.dragSegments_.filter(dragSegment => !(dragSegment[0].geometry instanceof Rectangle));
    super.handleUpEvent(...arguments);
    this.dragSegments_ = originalDragSegments;
  }

  // Don't allow addint points to the Line geometries (with max 2 points)
  _insertVertexCondition () {
    if (!this.vertexFeature_) {
      return true;
    }
    const vertexExtent = boundingExtent([ this.vertexFeature_.getGeometry().getCoordinates() ]);
    const segmentDataMatches = this.rBush_.getInExtent(vertexExtent);
    return !segmentDataMatches.some(segmentDataMatch => segmentDataMatch.geometry instanceof Line);
  }

  // @Overrides ol.interaction.Modify.handleEvent
  handleEvent (mapBrowserEvent) {
    if (!Object.values(MapBrowserEventType).includes(mapBrowserEvent.type)) {
      return true;
    }

    if (mapBrowserEvent.type === MapBrowserEventType.DBLCLICK) {
      return this._handleDblClickEvent(mapBrowserEvent);
    }
    if (mapBrowserEvent.type === MapBrowserEventType.POINTERDOWN) {
      this._cancelTextEdit();
    }

    return super.handleEvent(mapBrowserEvent);
  }

  _handleDblClickEvent (mapBrowserEvent) {
    let handled = false;

    const hitFeatures = mapBrowserEvent.map.getFeaturesAtPixel(mapBrowserEvent.pixel, {
      hitTolerance: this.pixelTolerance_,
      layerFilter: layer => layer === this._sketchLayer
    });
    const textFeature = hitFeatures.find(feature => isSketchTextFeature(feature) && this.features_.getArray().indexOf(feature) >= 0);
    if (textFeature) {
      this._startTextInput(mapBrowserEvent, textFeature);
      handled = true;
    }

    return !handled;
  }

  _startTextInput (mapBrowserEvent, textFeature) {
    const map = mapBrowserEvent.map;
    this._cancelTextEdit();
    this._textFeature = textFeature;
    this._textFeatureStyle = textFeature.getStyle();

    if (!this._sketchTextInput) {
      this._sketchTextInput = this._createSketchTextInput();
    }
    this._sketchTextInput.setStyle(this._textFeatureStyle);
    const coordinate = textFeature.getGeometry().getFirstCoordinate();
    const position = map.getPixelFromCoordinate(coordinate);
    textFeature.setStyle(new Style({})); // hide feature for the time of editing
    this._sketchTextInput.show(position, this._textFeatureStyle.getText().getText());
    this._repositionSketchTextInputListeners = animateDuringMapMove(map, () => { this._repositionSketchTextInput(map); });
  }

  _createSketchTextInput () {
    const sketchTextInput = new SketchTextInput();
    sketchTextInput.addEventListener('cancel', () => this._cancelTextEdit());
    sketchTextInput.addEventListener('finish', event => this._finishTextEdit(event.detail.text.trim()));
    this._mapTargetElement.append(sketchTextInput);
    return sketchTextInput;
  }

  _repositionSketchTextInput (map) {
    if (this._textFeature && this._sketchTextInput) {
      const coordinate = this._textFeature.getGeometry().getFirstCoordinate();
      this._sketchTextInput.position = map.getPixelFromCoordinate(coordinate);
    }
  }

  _cancelTextEdit () {
    unByKey(this._repositionSketchTextInputListeners);
    if (this._textFeature) {
      this._textFeature.setStyle(this._textFeatureStyle); // show the feature when editing is done
      this._textFeature.changed();
      this._textFeature = null;
      this._textFeatureStyle = null;
    }
    if (this._sketchTextInput) {
      this._sketchTextInput.hide();
    }
  }

  _finishTextEdit (text) {
    unByKey(this._repositionSketchTextInputListeners);
    if (this._textFeature) {
      setTextOnFeature(this._textFeature, text);
      this._textFeatureStyle.getText().setText(text);
      this._cancelTextEdit();
      this.dispatchEvent(new ModifyEvent(ModifyEventType.MODIFYEND, this.features_, this.lastPointerEvent_));
    }
  }

  _writeRectangleGeometry (feature, geometry) {
    const vertices = geometry.getVertices();
    const numberOfVertices = vertices.length;
    for (let vertexIndex = 0; vertexIndex < numberOfVertices; ++vertexIndex) {
      const vertex = vertices[vertexIndex];
      // The reason why we're creating the segment with 2 identical vertex is to prevent the user from creating extra vertices when dragging
      // an intermediate point on the segment, this is what happens in the polygon for example, but if we do that here it would convert our
      // rectangle into a polygon, breaking the rectangle, which we don't want, so we create the segment with 2 identical points.
      // This hack is also used for the center point of the circle inside of OL, so we inspired ourselves from there
      const segmentData = {
        feature,
        geometry,
        depth: [ vertexIndex ],
        index: vertexIndex,
        segment: [ vertex, vertex ]
      };
      this.rBush_.insert(geometry.getExtent(), segmentData);
    }
  }

  _shiftPressed () {
    return this.lastPointerEvent_ && this.lastPointerEvent_.originalEvent && this.lastPointerEvent_.originalEvent.shiftKey;
  }

}

// @Overrides closestOnSegmentData in ol.interaction.Modify
function closestOnSegmentData (pointCoordinates, segmentData) {
  const geometry = segmentData.geometry;
  if (geometry.getType() === GeometryType.CIRCLE && segmentData.index === CIRCLE_CIRCUMFERENCE_INDEX) {
    return geometry.getClosestPoint(pointCoordinates);
  }
  return closestOnSegment(pointCoordinates, segmentData.segment);
}

// @Overrides projectedDistanceToSegmentDataSquared in ol.interaction.Modify
function projectedDistanceToSegmentDataSquared (pointCoordinates, segmentData, projection) {
  const geometry = segmentData.geometry;

  if (geometry instanceof Oval && segmentData.index === CIRCLE_CIRCUMFERENCE_INDEX) {
    const closestPoint = geometry.getClosestPoint(pointCoordinates);
    return squaredCoordinateDistance(pointCoordinates, closestPoint);
  }

  if (geometry.getType() === GeometryType.CIRCLE) {
    const circleGeometry = /** @type {module:ol/geom/Circle} */ (geometry);
    if (segmentData.index === CIRCLE_CIRCUMFERENCE_INDEX) {
      const distanceToCenterSquared = squaredCoordinateDistance(circleGeometry.getCenter(), pointCoordinates);
      const distanceToCircumference = Math.sqrt(distanceToCenterSquared) - circleGeometry.getRadius();
      return distanceToCircumference * distanceToCircumference;
    }
  }
  const coordinate = fromUserCoordinate(pointCoordinates, projection);
  const tempSegment = [];
  tempSegment[0] = fromUserCoordinate(segmentData.segment[0], projection);
  tempSegment[1] = fromUserCoordinate(segmentData.segment[1], projection);
  return squaredDistanceToSegment(coordinate, tempSegment);
}

export function onFeaturesModifyStart (featureCollection) {
  featureCollection.forEach(feature => feature.set(FeatureProperty.OLD_GEOMETRY, feature.getGeometry().clone()));
}

async function onFeaturesModifyEnd (featuresBeingModified, source, mapTargetElement) {
  const areModificationsValid = await getWebWorkerInstance(mapTargetElement).areModificationsValid(featuresBeingModified, source.getFeatures())
    .catch(error => {
      logger.error('Error while checking geometry validity', error);
      return false;
    });
  if (areModificationsValid) {
    source.dispatchEvent(new CadenzaModifyEndEvent(featuresBeingModified));
  } else {
    featuresBeingModified.forEach(feature => feature.setGeometry(feature.get(FeatureProperty.OLD_GEOMETRY)));
  }
}

export class CadenzaModifyEndEvent extends Event {

  _features;

  constructor (features) {
    super(CadenzaModifyEventType.MODIFYEND);
    this._features = features;
  }

  get features () {
    return this._features;
  }

}

function isSketchTextFeature (feature) {
  const isTextSketchFeature = getSketchItemDto(feature)?.type === SketchItemType.TEXT;
  return isClassicTextSketchFeature(feature) || isTextSketchFeature;
}

function isClassicTextSketchFeature (feature) {
  return feature.get(SketchFeatureProperty.STYLE_ITEM) != null;
}

function setTextOnFeature (textFeature, text) {
  if (isClassicTextSketchFeature(textFeature)) {
    textFeature.get(SketchFeatureProperty.STYLE_ITEM).lines = text.split('\n');
  } else {
    const newSketchItemDto = getSketchItemDto(textFeature);
    newSketchItemDto.styles.text = text;
    setSketchItemDto(textFeature, newSketchItemDto);
  }
}
